// Lightweight client-side technical indicator math for SymbolChart overlays.
//
// All functions take an array of bars `[{d, o, h, l, c, v?}, ...]` (oldest → newest)
// and return either:
//   - a number[] of the same length (NaN where insufficient data)
//   - or an object of named series, also same length each
//
// Indicators are computed on close prices. Lookback is configurable per function
// where it makes sense. All implementations are pure and side-effect free.

const closes = (bars) => bars.map((b) => Number(b.c));

const sma = (xs, n) => {
  const out = new Array(xs.length).fill(NaN);
  let sum = 0;
  for (let i = 0; i < xs.length; i++) {
    sum += xs[i];
    if (i >= n) sum -= xs[i - n];
    if (i >= n - 1) out[i] = sum / n;
  }
  return out;
};

const std = (xs, n) => {
  const out = new Array(xs.length).fill(NaN);
  for (let i = n - 1; i < xs.length; i++) {
    const slice = xs.slice(i - n + 1, i + 1);
    const mean = slice.reduce((a, b) => a + b, 0) / n;
    const variance = slice.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
    out[i] = Math.sqrt(variance);
  }
  return out;
};

const ema = (xs, n) => {
  const out = new Array(xs.length).fill(NaN);
  if (!xs.length) return out;
  const k = 2 / (n + 1);
  // Seed with a simple average of the first n values
  let sum = 0;
  for (let i = 0; i < xs.length; i++) {
    if (i < n) {
      sum += xs[i];
      if (i === n - 1) out[i] = sum / n;
      continue;
    }
    out[i] = xs[i] * k + out[i - 1] * (1 - k);
  }
  return out;
};

// ---- public ----

export const computeSMA = (bars, period = 20) => sma(closes(bars), period);

export const computeBollinger = (bars, period = 20, k = 2) => {
  const xs = closes(bars);
  const mid = sma(xs, period);
  const sd = std(xs, period);
  const upper = mid.map((m, i) => (isFinite(m) ? m + k * sd[i] : NaN));
  const lower = mid.map((m, i) => (isFinite(m) ? m - k * sd[i] : NaN));
  return { upper, mid, lower };
};

// Wilder-style RSI (Wilder smoothing) — same algorithm as services/indicators/kce
export const computeRSI = (bars, period = 14) => {
  const xs = closes(bars);
  const out = new Array(xs.length).fill(NaN);
  if (xs.length < period + 1) return out;
  let gain = 0;
  let loss = 0;
  for (let i = 1; i <= period; i++) {
    const d = xs[i] - xs[i - 1];
    if (d >= 0) gain += d;
    else loss -= d;
  }
  let avgG = gain / period;
  let avgL = loss / period;
  const rsiFor = () => {
    if (avgL === 0) return avgG === 0 ? 50 : 100;
    const rs = avgG / avgL;
    return 100 - 100 / (1 + rs);
  };
  out[period] = rsiFor();
  for (let i = period + 1; i < xs.length; i++) {
    const d = xs[i] - xs[i - 1];
    const g = d > 0 ? d : 0;
    const l = d < 0 ? -d : 0;
    avgG = (avgG * (period - 1) + g) / period;
    avgL = (avgL * (period - 1) + l) / period;
    out[i] = rsiFor();
  }
  return out;
};

// MACD — fast EMA - slow EMA, with signal EMA on the MACD line
export const computeMACD = (bars, fast = 12, slow = 26, signal = 9) => {
  const xs = closes(bars);
  const emaFast = ema(xs, fast);
  const emaSlow = ema(xs, slow);
  const macd = emaFast.map((f, i) => (isFinite(f) && isFinite(emaSlow[i]) ? f - emaSlow[i] : NaN));
  // signal = EMA of macd, skipping leading NaN
  const sig = new Array(macd.length).fill(NaN);
  const k = 2 / (signal + 1);
  let started = false;
  let seedSum = 0;
  let seedCount = 0;
  for (let i = 0; i < macd.length; i++) {
    if (!isFinite(macd[i])) continue;
    if (!started) {
      seedSum += macd[i];
      seedCount += 1;
      if (seedCount === signal) {
        sig[i] = seedSum / signal;
        started = true;
      }
      continue;
    }
    sig[i] = macd[i] * k + sig[i - 1] * (1 - k);
  }
  const hist = macd.map((m, i) => (isFinite(m) && isFinite(sig[i]) ? m - sig[i] : NaN));
  return { macd, signal: sig, hist };
};
